import type { AccentColor, AccentColorTheme } from "@/types";

// ─── Currencies ───────────────────────────────────────────────

/**
 * Canonical list of supported currencies. Used by the Currency Converter
 * widget for both source and target dropdowns.
 *
 * `CURRENCY_OPTIONS` is a curated subset shown in widgets like Zakat,
 * Loan, and the EmbedConfigurator currency selector. Every entry in
 * `CURRENCY_OPTIONS` MUST also exist in `POPULAR_CURRENCIES` (enforced
 * by `src/test/tools.test.ts`).
 */
export const POPULAR_CURRENCIES = [
  "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "CNY", "INR", "PKR",
  "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "EGP", "TRY", "MYR", "SGD",
  "BRL", "ZAR", "KRW", "MXN", "IDR", "THB", "NGN", "BDT", "PHP", "VND",
  "KES", "GHS",
];

/** Curated subset shown in EmbedConfigurator + ZakatCalculator selectors. */
export const CURRENCY_OPTIONS = [
  "USD", "EUR", "GBP", "PKR", "INR", "AED", "SAR", "CAD", "AUD",
  "MYR", "QAR", "KWD", "BHD", "EGP", "TRY", "BDT", "IDR",
  "SGD", "OMR", "JPY", "CHF", "ZAR", "NGN", "KES", "GHS",
];

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$", EUR: "€", GBP: "£", PKR: "₨", INR: "₹", AED: "د.إ",
  SAR: "﷼", CAD: "C$", AUD: "A$", MYR: "RM", QAR: "﷼", KWD: "د.ك",
  BHD: ".د.ب", EGP: "E£", TRY: "₺", BDT: "৳", IDR: "Rp",
  SGD: "S$", OMR: "﷼", JPY: "¥", CHF: "CHF", ZAR: "R", NGN: "₦", KES: "KSh", GHS: "GH₵",
  CNY: "¥", BRL: "R$", KRW: "₩", MXN: "Mex$", THB: "฿", PHP: "₱", VND: "₫",
};

// ─── Tools with currency support ──────────────────────────────

export const TOOLS_WITH_CURRENCY = [
  "zakat", "currency", "loan", "tip", "discount",
  "borrowing-power", "stamp-duty", "rent-vs-buy", "profit-loss",
] as const;

// NOTE: `NOJS_HEIGHTS` previously lived here. It has been migrated to the
// canonical `ToolMeta.noJsHeight` field in `src/lib/tools.ts` so the No-JS
// embed height stays colocated with each tool's other metadata.

// ─── Accent colors ────────────────────────────────────────────

/** Accent color swatches used in EmbedConfigurator */
export const ACCENT_COLORS: AccentColor[] = [
  { name: "Emerald", value: "emerald", hsl: "160, 84%, 39%" },
  { name: "Blue", value: "blue", hsl: "210, 100%, 52%" },
  { name: "Violet", value: "violet", hsl: "262, 80%, 55%" },
  { name: "Rose", value: "rose", hsl: "350, 80%, 55%" },
  { name: "Amber", value: "amber", hsl: "38, 92%, 50%" },
  { name: "Slate", value: "slate", hsl: "220, 14%, 46%" },
];

/** Accent color HSL values for embed mode (light/dark variants) */
export const ACCENT_COLOR_THEMES: Record<string, AccentColorTheme> = {
  emerald: { light: "160 84% 39%", dark: "160 84% 45%" },
  blue:    { light: "210 100% 52%", dark: "210 100% 58%" },
  violet:  { light: "262 80% 55%", dark: "262 80% 60%" },
  rose:    { light: "350 80% 55%", dark: "350 80% 60%" },
  amber:   { light: "38 92% 50%", dark: "38 92% 55%" },
  slate:   { light: "220 14% 46%", dark: "220 14% 55%" },
};

// ─── External API base URLs ──────────────────────────────────

export const API_URLS = {
  frankfurter: "https://api.frankfurter.dev/v1",
  openExchangeRate: "https://open.er-api.com/v6/latest",
  aladhan: "https://api.aladhan.com/v1",
  aladhanFallback: "https://aladhan.api.islamic.network/v1",
  /** Gold-API free tier, keyless public endpoint, no API key required or stored */
  goldApi: "https://api.gold-api.com/price",
  qrServer: "https://api.qrserver.com/v1/create-qr-code",
} as const;

// ─── Zakat constants ─────────────────────────────────────────

export const GOLD_NISAB_GRAMS = 85;
// Silver nisab: 200 dirhams × 3.0618 g = 612.36 g (scholarly standard).
// Previously 595 g, that shortfall lowered the threshold and incorrectly
// triggered Zakat above wealth that had not yet reached nisab.
export const SILVER_NISAB_GRAMS = 612.36;
export const TROY_OZ_TO_GRAM = 31.1035;
export const DEFAULT_GOLD_PRICE_PER_GRAM = 75;
export const DEFAULT_SILVER_PRICE_PER_GRAM = 0.95;

// ─── Embed base URL ──────────────────────────────────────────

export const EMBED_BASE_URL = "https://embedkit.tech";

/** Branded 1200x630 share image used when a page has no image of its own. */
export const SITE_OG_IMAGE = `${EMBED_BASE_URL}/og/embedkit.jpg`;

// ─── Embed iframe sandbox ────────────────────────────────────

/**
 * `sandbox` attribute of every embed iframe: the three generated snippets, the
 * dashboard previews and (as a copy, because it is a static file that cannot
 * import this) public/embedkit-loader.js. A unit test keeps the copy equal.
 *
 * Only real sandbox tokens belong here. Clipboard access is the separate
 * `allow="clipboard-write"` attribute: `allow-clipboard-write` is not a sandbox
 * flag, and browsers log a console error for it on the embedding page (#70).
 * `allow-downloads` is what lets the QR, Certificate, Favicon and Placeholder
 * Image widgets save a file. It permits any download the framed page starts, with
 * or without a click (MDN; there is no gesture-only token, Chrome rejects
 * `allow-downloads-without-user-activation`), so it is a real, if small, widening.
 */
export const EMBED_IFRAME_SANDBOX = "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads";
